import { useState } from 'react';
import { useParams } from 'react-router-dom';
import DashboardHeader from '../../../shared/components/DashboardHeader';
import {
  ASSESSMENT_COMPONENTS,
  ASSESSMENT_TABS as TABS,
  ATTENDANCE_TAB,
} from '../../../shared/constant/assessmentTabs';
import { getGradeBand } from '../../../shared/constant/grading';
import { TERMS } from '../../../shared/constant/terms';
import { useOwnSubjectRecord } from '../hooks/useOwnSubjectRecord';

const ATTENDANCE_STATUSES = [
  { value: 'present', label: 'Present', dot: 'bg-green-500', tone: 'bg-green-50 text-green-600' },
  { value: 'late', label: 'Late', dot: 'bg-amber-500', tone: 'bg-amber-50 text-amber-600' },
  { value: 'absent', label: 'Absent', dot: 'bg-red-500', tone: 'bg-red-50 text-red-600' },
  { value: 'excused', label: 'Excused', dot: 'bg-[#4ea6d0]', tone: 'bg-blue-50 text-blue-600' },
];

const formatPercent = (value) => (value == null ? '—' : `${Number(value).toFixed(2)}%`);

const formatDate = (date) =>
  date
    ? new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

// Attendance dates also name the weekday, e.g. "Sep 21, 2026 - Mon".
const formatDateWithDay = (date) =>
  date
    ? `${formatDate(date)} - ${new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short' })}`
    : '—';

function AttendanceSection({ records }) {
  const counts = Object.fromEntries(
    ATTENDANCE_STATUSES.map((s) => [s.value, records.filter((r) => r.status === s.value).length])
  );
  // Each status is shown on its own, as its share of all the days recorded.

  if (records.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
        No attendance recorded for this term yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2">
        {ATTENDANCE_STATUSES.map((s) => (
          <div key={s.value} className="bg-white rounded-lg border border-gray-200 p-2.5">
            <p className="flex items-center gap-1 text-xs text-gray-500">
              <span className={`h-2 w-2 shrink-0 rounded-full ${s.dot}`} aria-hidden="true" />
              {s.label}
            </p>
            <p className="text-xl font-bold text-[#0b4a71]">{counts[s.value]}</p>
            <p className="text-[11px] text-gray-500">{formatPercent((counts[s.value] / records.length) * 100)}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#4ea6d0] text-left text-white">
            <tr>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {records.map((record) => {
              const status = ATTENDANCE_STATUSES.find((s) => s.value === record.status);
              return (
                <tr key={record.attendance_date}>
                  <td className="px-4 py-3 text-gray-700">{formatDateWithDay(record.attendance_date)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${status?.tone ?? ''}`}>
                      {status?.label ?? record.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AssessmentSection({ items, itemLabel }) {
  if (items.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
        No {itemLabel} scores for this term yet.
      </div>
    );
  }

  const totalScore = items.reduce((sum, i) => sum + i.score, 0);
  const totalMax = items.reduce((sum, i) => sum + i.maxScore, 0);

  return (
    <>
      {/* Phones get a card per item; the table takes over from the sm breakpoint. */}
      <div className="space-y-2 sm:hidden">
        {items.map((item) => (
          <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-[#0b4a71] break-words">{item.title}</p>
                <p className="text-xs text-gray-500">{formatDate(item.date)}</p>
              </div>
              <p className="shrink-0 font-semibold text-gray-700">
                {item.score} / {item.maxScore}
              </p>
            </div>
            {item.comment && (
              <p className="border-t border-gray-100 pt-2 text-sm text-gray-600 break-words whitespace-pre-line">
                <span className="block font-medium text-gray-700">Comment</span>
                {item.comment}
              </p>
            )}
          </div>
        ))}

        <div className="flex items-center justify-between rounded-lg border border-[#4ea6d0]/40 bg-[#4ea6d0]/20 p-4">
          <p className="font-medium text-[#0b4a71]">Total</p>
          <p className="font-semibold text-gray-700">
            {totalScore} / {totalMax}
          </p>
        </div>
      </div>

      <div className="hidden sm:block bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#4ea6d0] text-left text-white">
            <tr>
              <th className="px-4 py-3 font-medium">Title</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Score</th>
              <th className="px-4 py-3 font-medium">Percent</th>
              <th className="px-4 py-3 font-medium">Comment</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-3 font-medium text-[#0b4a71]">{item.title}</td>
                <td className="px-4 py-3 text-gray-600">{formatDate(item.date)}</td>
                <td className="px-4 py-3 text-gray-700">
                  {item.score} / {item.maxScore}
                </td>
                <td className="px-4 py-3 text-gray-600">{formatPercent((item.score / item.maxScore) * 100)}</td>
                <td className="px-4 py-3 text-gray-600 min-w-40 whitespace-pre-line">{item.comment || '—'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-[#4ea6d0]/40 bg-[#4ea6d0]/20">
            <tr>
              <td className="px-4 py-3 font-medium text-[#0b4a71]" colSpan={2}>Total</td>
              <td className="px-4 py-3 font-medium text-gray-700">
                {totalScore} / {totalMax}
              </td>
              <td className="px-4 py-3 font-medium text-[#0b4a71]">{formatPercent((totalScore / totalMax) * 100)}</td>
              <td className="px-4 py-3" />
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

function SubjectPerStudentPage() {
  const { subjectId } = useParams();
  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [term, setTerm] = useState(String(TERMS[0]));
  const { subject, loading, error } = useOwnSubjectRecord(subjectId);

  const termNumber = Number(term);
  const termRow = subject?.grades[termNumber];
  const termGrade = termRow?.term_grade ?? null;
  const band = getGradeBand(termGrade);
  const config = ASSESSMENT_COMPONENTS[activeTab];

  return (
    <div className="h-dvh w-full flex flex-col bg-appbg overflow-hidden">
      <DashboardHeader title="Subject" backTo="/student-dashboard" showStudentMenu />

      <div className="flex-1 min-h-0 overflow-y-auto">
        <main className="w-full p-4 lg:w-3/4 lg:mx-auto">
          {loading ? (
            <p className="w-full text-center text-gray-500 py-12">Loading…</p>
          ) : error ? (
            <p className="w-full text-center text-red-600 py-12">{error}</p>
          ) : (
            <div className="w-full space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-semibold text-[#0b4a71]">{subject.name}</h2>
                  <p className="text-md text-gray-500">{subject.category} - {subject.teacher}</p>
                </div>

                <select
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  aria-label="Term"
                  className="shrink-0 rounded-md border border-[#4ea6d0] bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
                >
                  {TERMS.map((t) => (
                    <option key={t} value={t}>Term {t}</option>
                  ))}
                </select>
              </div>

              <div className="bg-[#0b4a71] rounded-lg p-5 text-center">
                <p className="text-sm text-white/70">Term {term} Grade</p>
                <p className="text-4xl font-bold text-white">{termGrade ?? '—'}</p>
                {band && (
                  <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${band.tone}`}>
                    {band.remark}
                  </span>
                )}
              </div>

              <div className="flex gap-2 overflow-x-auto">
                {TABS.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium border transition ${
                      activeTab === tab
                        ? 'bg-[#4ea6d0] border-[#4ea6d0] text-white'
                        : 'border-gray-300 text-gray-600 hover:border-[#4ea6d0] hover:text-[#0b4a71]'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {activeTab === ATTENDANCE_TAB ? (
                <AttendanceSection records={subject.attendance.filter((r) => r.term === termNumber)} />
              ) : (
                <AssessmentSection
                  items={subject.assessments.filter((a) => a.component === config.component && a.term === termNumber)}
                  itemLabel={config.itemLabel}
                />
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default SubjectPerStudentPage;
